import React, { useEffect, useState } from 'react';
import Button from '../../common/Button';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import ToggleSwitch from '../../common/Switch';
import BottomDrawer from '../../common/BottomDrawer';
import SideDrawer from '../../common/SideDrawer';
import CreateHavalaDialog from './CreateHavalaDialog';
import CreateHavalaSideDrawerDialog from './CreateHavalaSideDrawerDialog';
import CustomTable from '../../common/CustomTable';
import { formatDateOnly } from '../../../utils/util';
import { HAVALA_PL_DISPLAY, HAVALA_PL_PARTY_ID } from '../../../utils/havalaPl';
import apiService from '../../../services/ApiService';
import { apiRoutes } from '../../../shared/apiRoutes';
import { useAuthorization } from '../../../auth/useAuthorization';
import { HAVLA_CREATE, HAVLA_DELETE, HAVLA_EDIT } from '../../../auth/permissions';

interface HavalaEntry {
  id: number;
  senderPartyId?: number | null;
  receiverPartyId?: number | null;
  senderPartyCode?: string;
  senderPartyName?: string;
  senderParty?: { id: number; code: string; name: string };
  receiverPartyCode?: string;
  receiverPartyName?: string;
  receiverParty?: { id: number; code: string; name: string };
  amount: number;
  city?: string;
  token?: string;
  remarks?: string;
  isDeleted?: boolean;
  createdAt?: string | Date; // Creation date from API
  modifiedBy?: string | { id: number; name: string; username?: string };
  modifiedOn?: string;
  modifiedAt?: string;
  /** Business date (Havala.RecordDate); may differ from createdAt */
  recordDate?: string | Date;
}

interface HavalaListRequest {
  page: number;
  pageSize: number;
  senderPartyId?: number;
  receiverPartyId?: number;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
  includeDeleted?: boolean;
}

interface HavalaListResponse {
  data: HavalaEntry[];
  total: number;
  totalCount?: number;
}

const rowsPerPageOptions = [
  { value: 10, label: '10' },
  { value: 25, label: '25' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
];

const HavalaAccount: React.FC = () => {
  const { hasPermission } = useAuthorization();
  const canCreateHavala = hasPermission(HAVLA_CREATE);
  const canEditHavala = hasPermission(HAVLA_EDIT);
  const canDeleteHavala = hasPermission(HAVLA_DELETE);

  const [entries, setEntries] = useState<HavalaEntry[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false); // Bottom drawer (for ALT+H)
  const [sideDrawerOpen, setSideDrawerOpen] = useState(canCreateHavala); // Right drawer opens on this page for users who can create
  const [editId, setEditId] = useState<number | null>(null);
  const [sideDrawerEditId, setSideDrawerEditId] = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sortColumn, setSortColumn] = useState<keyof HavalaEntry>('modifiedOn');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [lastEditedRowIndex, setLastEditedRowIndex] = useState<number | null>(null);
  const [showDeleted, setShowDeleted] = useState(true);
  const [listRequest, setListRequest] = useState<HavalaListRequest>({
    page: 1,
    pageSize: 25,
    orderBy: 'modifiedAt',
    orderDirection: 'desc',
    includeDeleted: true,
  });

  // Alt+H opens the bottom Create Havala drawer on this page. Alt+M opens the side drawer.
  useEffect(() => {
    const handleOpenCreateHavala = (e: Event) => {
      if (!canCreateHavala) return;
      e.stopImmediatePropagation();
      setDialogOpen(true);
      setEditId(null);
    };
    const handleOpenCreateHavalaSide = () => {
      if (!canCreateHavala) return;
      setSideDrawerEditId(null);
      setSideDrawerOpen(true);
    };

    window.addEventListener('open-create-havala', handleOpenCreateHavala as EventListener, true);
    window.addEventListener('open-create-havala-side', handleOpenCreateHavalaSide);

    return () => {
      window.removeEventListener('open-create-havala', handleOpenCreateHavala as EventListener, true);
      window.removeEventListener('open-create-havala-side', handleOpenCreateHavalaSide);
    };
  }, [canCreateHavala]);

  // Listen for Delete key to delete selected row (ALT+H is now handled globally in Header.tsx)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if on Havala Account page
      const currentPath = window.location.pathname;
      if (currentPath !== '/report/havala') {
        return;
      }

      // Don't trigger if typing in input fields
      const activeElement = document.activeElement;
      const isInputFocused = activeElement?.tagName === 'INPUT' || 
                             activeElement?.tagName === 'TEXTAREA' ||
                             activeElement?.getAttribute('contenteditable') === 'true';
      
      if (isInputFocused) {
        return;
      }

      // Don't trigger if drawers are open
      if (dialogOpen || sideDrawerOpen || confirmOpen) {
        return;
      }

      // Delete key - delete selected row (only if user has permission)
      if (canDeleteHavala && (e.key === 'Delete' || e.key === 'Backspace')) {
        // Only if a row is selected and we have entries
        if (selectedRowIndex !== null && selectedRowIndex >= 0 && selectedRowIndex < entries.length) {
          const selectedEntry = entries[selectedRowIndex];
          if (selectedEntry && selectedEntry.id) {
            e.preventDefault();
            e.stopPropagation();
            handleDelete(selectedEntry.id);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [selectedRowIndex, entries, dialogOpen, sideDrawerOpen, confirmOpen, canDeleteHavala]);

  // Listen for havala-saved event to refresh list (when saved from global dialog)
  useEffect(() => {
    const handleHavalaSaved = () => {
      // Refresh the list when a Havala is saved (even if opened from global dialog)
      fetchEntries();
    };

    window.addEventListener('havala-saved', handleHavalaSaved as EventListener);

    return () => {
      window.removeEventListener('havala-saved', handleHavalaSaved as EventListener);
    };
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [listRequest, currentPage, rowsPerPage]);

  const handleShowDeletedChange = (checked: boolean) => {
    setShowDeleted(checked);
    setListRequest(prev => ({ ...prev, includeDeleted: checked, page: 1 }));
    setCurrentPage(0);
  };

  // Focus on first row when entries load and no row is selected
  useEffect(() => {
    if (entries.length > 0 && selectedRowIndex === null && !loading && !dialogOpen && !sideDrawerOpen) {
      setTimeout(() => {
        setSelectedRowIndex(0);
      }, 100);
    }
  }, [entries.length, loading, dialogOpen, sideDrawerOpen]);

  const fetchEntries = () => {
    setLoading(true);
    const requestData: HavalaListRequest = {
      ...listRequest,
      page: currentPage + 1, // API expects 1-based page index
      pageSize: rowsPerPage,
    };

    apiService.post<HavalaListResponse>(apiRoutes.transactions.listHavala, requestData)
      .then((response) => {
        const rawData = response.data || [];
        const total = response.total || response.totalCount || 0;
        
        // Normalize the data to handle nested objects (especially modifiedBy)
        const normalizedData: HavalaEntry[] = rawData.map((item: any) => ({
          ...item,
          // Extract sender party info from nested object if present
          senderPartyCode: item.senderPartyCode || item.senderParty?.code || '',
          senderPartyName: item.senderPartyName || item.senderParty?.name || '',
          // Extract receiver party info from nested object if present
          receiverPartyCode: item.receiverPartyCode || item.receiverParty?.code || '',
          receiverPartyName: item.receiverPartyName || item.receiverParty?.name || '',
          // Extract modifiedBy as string if it's an object
          modifiedBy: typeof item.modifiedBy === 'object' && item.modifiedBy !== null
            ? (item.modifiedBy.name || item.modifiedBy.username || '')
            : (item.modifiedBy || ''),
        }));
        
        setEntries(normalizedData);
        setTotalPages(Math.ceil(total / rowsPerPage));
        setLoading(false);
      })
      .catch((error: any) => {
        console.error('Error fetching Havala entries:', error);
        window.dispatchEvent(new CustomEvent('show-toast', {
          detail: { message: 'Failed to load Havala entries', type: 'error' }
        }));
        setLoading(false);
      });
  };

  // Handle filter changes
  const handleFilter = (appliedFilters: Record<string, string>) => {
    const newRequest: Partial<HavalaListRequest> = {
      page: 1,
    };

    // Handle sender party filter if needed
    if (appliedFilters['senderPartyId']) {
      newRequest.senderPartyId = parseInt(appliedFilters['senderPartyId']);
    }

    // Handle receiver party filter if needed
    if (appliedFilters['receiverPartyId']) {
      newRequest.receiverPartyId = parseInt(appliedFilters['receiverPartyId']);
    }

    setListRequest(prev => ({
      ...prev,
      ...newRequest,
    }));
    setCurrentPage(0);
  };

  const handleDelete = (id: number) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    
    try {
      await apiService.delete(apiRoutes.transactions.deleteHavala(deleteId));
      setEntries(prev => prev.filter(e => e.id !== deleteId));
      setConfirmOpen(false);
      setDeleteId(null);
      fetchEntries(); // Refresh the list
      window.dispatchEvent(new CustomEvent('show-toast', { 
        detail: { message: 'Havala transaction deleted successfully', type: 'success' } 
      }));
    } catch (error: any) {
      console.error('Error deleting Havala transaction:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete Havala transaction';
      window.dispatchEvent(new CustomEvent('show-toast', { 
        detail: { message: errorMessage, type: 'error' } 
      }));
    }
  };

  const handleCreate = () => {
    setSideDrawerEditId(null);
    setSideDrawerOpen(true);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setListRequest(prev => ({
      ...prev,
      pageSize: newRowsPerPage,
    }));
    setCurrentPage(0);
  };

  // Handle sorting
  const handleSort = (column: keyof HavalaEntry) => {
    // Map column keys to API field names
    const columnToApiField: Record<string, string> = {
      'senderPartyCode': 'senderPartyId',
      'receiverPartyCode': 'receiverPartyId',
      'amount': 'amount',
      'city': 'city',
      'modifiedBy': 'modifiedBy',
      'modifiedOn': 'modifiedAt',
    };

    const apiField = columnToApiField[String(column)] || String(column);
    const newDirection = sortColumn === column && sortDirection === 'desc' ? 'asc' : 'desc';

    setSortColumn(column);
    setSortDirection(newDirection);
    setListRequest(prev => ({
      ...prev,
      orderBy: apiField,
      orderDirection: newDirection,
      page: 1,
    }));
    setCurrentPage(0);
  };

  const isPlHavalaSide = (partyId: number | null | undefined, nested?: { id?: number | null }): boolean => {
    if (partyId === HAVALA_PL_PARTY_ID || partyId === null) return true;
    if (nested?.id === HAVALA_PL_PARTY_ID) return true;
    return false;
  };

  // Format sender display: "Party Code - Party Name", or P&L when id is null / 0
  const formatSender = (item: HavalaEntry): string => {
    if (isPlHavalaSide(item.senderPartyId, item.senderParty)) {
      return HAVALA_PL_DISPLAY;
    }
    // Handle nested senderParty object
    if (item.senderParty) {
      const code = item.senderParty.code || '';
      const name = item.senderParty.name || '';
      return name ? `${code} - ${name}` : code || 'N/A';
    }
    // Handle flat fields
    const code = item.senderPartyCode || '';
    const name = item.senderPartyName || '';
    return name ? `${code} - ${name}` : code || 'N/A';
  };

  // Format receiver display: "Party Code - Party Name", or P&L when id is null / 0
  const formatReceiver = (item: HavalaEntry): string => {
    if (isPlHavalaSide(item.receiverPartyId, item.receiverParty)) {
      return HAVALA_PL_DISPLAY;
    }
    // Handle nested receiverParty object
    if (item.receiverParty) {
      const code = item.receiverParty.code || '';
      const name = item.receiverParty.name || '';
      return name ? `${code} - ${name}` : code || 'N/A';
    }
    // Handle flat fields
    const code = item.receiverPartyCode || '';
    const name = item.receiverPartyName || '';
    return name ? `${code} - ${name}` : code || 'N/A';
  };

  // Format modifiedBy - handle both string and object
  const formatModifiedBy = (item: HavalaEntry): string => {
    if (!item.modifiedBy) return '-';
    if (typeof item.modifiedBy === 'string') return item.modifiedBy;
    if (typeof item.modifiedBy === 'object') {
      return item.modifiedBy.name || item.modifiedBy.username || '-';
    }
    return '-';
  };

  return (
    <div className="p-4 w-full mx-auto bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen">
      <div className="bg-white rounded-lg shadow-md border border-slate-200 overflow-hidden mb-4">
      <CustomTable<HavalaEntry>
        variant="gameList"
        data={entries}
        title='Havala'
        headerExtra={
          <label className="flex items-center gap-2 cursor-pointer">
            <ToggleSwitch
              enabled={showDeleted}
              onChange={handleShowDeletedChange}
            />
            <span className="text-xs sm:text-sm font-medium text-slate-700 whitespace-nowrap">Show deleted</span>
          </label>
        }
        getRowClassName={(item, index) => {
          const entry = item as HavalaEntry;
          if (entry.isDeleted) {
            const alternating = index !== undefined && index % 2 === 1 ? 'bg-slate-100' : 'bg-slate-200/80';
            return `${alternating} [&_td]:opacity-80 [&_td]:blur-[0.5px]`;
          }
          return '';
        }}
        getRowActions={(item) => {
          const entry = item as HavalaEntry;
          if (entry.isDeleted) return { showEdit: false, showDelete: false };
          return { showEdit: true, showDelete: true };
        }}
        columns={[
          {
            key: 'receiverPartyCode',
            label: 'Receiver',
            sortable: true,
            render: (item) => formatReceiver(item as HavalaEntry),
          },
          {
            key: 'senderPartyCode',
            label: 'Sender',
            sortable: true,
            render: (item) => formatSender(item),
          },
          { 
            key: 'amount', 
            label: 'Amount',
            sortable: true,
            align: 'right',
            render: (item) => {
              const amount = item.amount ? (item.amount / 100) : 0;
              return (
                <div className="text-right mr-4">
                  {amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              );
            },
          },
          { 
            key: 'city', 
            label: 'City',
            sortable: true,
            render: (item) => item.city || '-',
          },
          {
            key: 'recordDate',
            label: 'Record date',
            sortable: true,
            render: (item) => {
              const entry = item as HavalaEntry;
              const d = entry.recordDate;
              return d ? (formatDateOnly(d) || '-') : '-';
            },
          },
          { 
            key: 'remarks', 
            label: 'Remarks',
            sortable: true,
            render: (item) => {
              return (
                <div className="text-left" title={item.remarks}>
                  {item.remarks ? `${item.remarks.substring(0, 20)}...` : '-'}
                </div>
              );
            },
          },
          { 
            key: 'token', 
            label: 'Token',
            sortable: true,
            render: (item) => item.token || '-',
          },
          { 
            key: 'modifiedBy', 
            label: 'Modified By',
            sortable: true,
            render: (item) => formatModifiedBy(item),
          },
          {
            key: 'modifiedOn',
            label: 'Modified On',
            sortable: true,
            filterable: true,
            filterType: 'date',
            render: (item) => {
              const entry = item as HavalaEntry;
              const dateValue = entry.modifiedAt || entry.modifiedOn;
              const formattedDate = dateValue ? (formatDateOnly(dateValue) || '-') : '-';
              
              // If deleted, show (deleted) at end; else show (Modified) if modified
              const createdAt = entry.createdAt ? new Date(entry.createdAt).getTime() : null;
              const modifiedAtTime = entry.modifiedAt ? new Date(entry.modifiedAt).getTime() : null;
              const isModified = createdAt !== null && modifiedAtTime !== null && createdAt !== modifiedAtTime;
              
              return (
                <div className="text-center">
                  {formattedDate}
                  {entry.isDeleted ? (
                    <span className="ml-1 text-red-500 font-medium">(deleted)</span>
                  ) : isModified ? (
                    <span className="text-amber-500 ml-1">(Modified)</span>
                  ) : null}
                </div>
              );
            },
          },
        ]}
        onSort={handleSort}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        headerButtons={canCreateHavala ? [
          { label: "Create New", onClick: handleCreate },
        ] : []}
        totalPages={totalPages}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        loading={loading}
        onEdit={canEditHavala ? (id) => {
          // Find the index of the row being edited
          const rowIndex = entries.findIndex(item => item.id === id);
          if (rowIndex !== -1) {
            setLastEditedRowIndex(rowIndex);
          }
          setSideDrawerEditId(id);
          setSideDrawerOpen(true);
        } : undefined}
        onDelete={canDeleteHavala ? handleDelete : undefined}
        getId={(item) => item.id}
        onFilter={handleFilter}
        initialSelectedRowIndex={selectedRowIndex}
        onRowSelectionChange={setSelectedRowIndex}
      />
      </div>

      {/* Create/Edit Right Drawer (for Create New button and Edit icon) */}
      <SideDrawer
        isOpen={sideDrawerOpen}
        onClose={() => {
          setSideDrawerOpen(false);
          const wasEdit = sideDrawerEditId !== null;
          setSideDrawerEditId(null);
          // Restore focus after drawer closes
          setTimeout(() => {
            if (wasEdit && lastEditedRowIndex !== null && lastEditedRowIndex >= 0 && lastEditedRowIndex < entries.length) {
              // Restore focus to the row that was being edited
              setSelectedRowIndex(lastEditedRowIndex);
              setLastEditedRowIndex(null);
            } else {
              // Focus on first row when add form is closed
              if (entries.length > 0) {
                setSelectedRowIndex(0);
              }
            }
          }, 100);
        }}
        title={sideDrawerEditId ? "Edit Havala" : "Create Havala"}
        width="w-[min(460px,calc(100vw-1rem))]"
      >
        <CreateHavalaSideDrawerDialog
          isOpen={sideDrawerOpen}
          editId={sideDrawerEditId}
          onClose={() => {
            setSideDrawerOpen(false);
            const wasEdit = sideDrawerEditId !== null;
            setSideDrawerEditId(null);
            // Restore focus after drawer closes
            setTimeout(() => {
              if (wasEdit && lastEditedRowIndex !== null && lastEditedRowIndex >= 0 && lastEditedRowIndex < entries.length) {
                // Restore focus to the row that was being edited
                setSelectedRowIndex(lastEditedRowIndex);
                setLastEditedRowIndex(null);
              } else {
                // Focus on first row when add form is closed
                if (entries.length > 0) {
                  setSelectedRowIndex(0);
                }
              }
            }, 100);
          }}
          onSuccess={() => {
            // Refresh the list after successful save
            fetchEntries();
          }}
        />
      </SideDrawer>

      {/* Create/Edit Bottom Drawer (for ALT+H shortcut only) */}
      <BottomDrawer
        isOpen={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditId(null);
          // Restore focus after dialog closes
          setTimeout(() => {
            // Focus on first row when form is closed
            if (entries.length > 0) {
              setSelectedRowIndex(0);
            }
          }, 100);
        }}
        title={editId ? "Edit Havala" : "Create Havala"}
        maxHeight="max-h-[min(85dvh,640px)] sm:max-h-[min(70vh,560px)]"
      >
        <CreateHavalaDialog
          isOpen={dialogOpen}
          editId={editId}
          onClose={() => {
            setDialogOpen(false);
            setEditId(null);
            // Restore focus after dialog closes
            setTimeout(() => {
              // Focus on first row when form is closed
              if (entries.length > 0) {
                setSelectedRowIndex(0);
              }
            }, 100);
          }}
          onSuccess={() => {
            // Refresh the list after successful save
            fetchEntries();
          }}
        />
      </BottomDrawer>

      {/* Delete Confirmation */}
      <ConfirmationDialog
        isOpen={confirmOpen}
        title="Delete Confirmation"
        message="Are you sure you want to delete this entry?"
        onConfirm={confirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default HavalaAccount;